import { useCallback, useEffect, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { MonthStatusBadge } from "./MonthDaysPanel";
import { EmployeeMonthDrawer } from "./EmployeeMonthDrawer";
import { currentPeriodIST, shiftPeriod, periodLabel, fmtHours } from "../../lib/regularisation";

export function MonthRows({ rows, onOpen, selectable, selected, setSelected }) {
    return (
        <table className="w-full text-sm" data-testid="month-rows-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                    {selectable && <th className="w-8 px-2" />}
                    {["Employee", "Status", "Full / half", "Leave", "Absence", "Pending (emp / appr)", "Hours vs expected", "Paid days", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-400">No employees.</td></tr>}
                {rows.map((r) => (
                    <tr key={r.employee.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`month-row-${r.employee.id}`}>
                        {selectable && (
                            <td className="px-2">
                                <input type="checkbox" disabled={!r.ready_to_finalise} checked={selected.includes(r.employee.id)}
                                    onChange={(e) => setSelected((s) => e.target.checked ? [...s, r.employee.id] : s.filter((x) => x !== r.employee.id))}
                                    data-testid={`month-row-check-${r.employee.id}`} />
                            </td>
                        )}
                        <td className="px-3 py-2 text-xs"><div className="text-slate-800 font-medium">{r.employee.full_name}</div><div className="text-[10px] text-slate-400 font-mono">{r.employee.employee_code}</div></td>
                        <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-1">
                                <MonthStatusBadge status={r.month_status} testId={`month-row-status-${r.employee.id}`} />
                                {r.pending_count > 0 && r.month_status !== "Finalised" && (
                                    <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-800" title="Pending dates hold this employee's finalisation" data-testid={`month-row-held-${r.employee.id}`}>
                                        Held · {r.pending_count} pending
                                    </span>
                                )}
                            </div>
                        </td>
                        <td className="px-3 py-2 text-xs tabular-nums">{r.days.worked_full_days} / {r.days.worked_half_days}</td>
                        <td className="px-3 py-2 text-xs tabular-nums">{r.days.leave_days}</td>
                        <td className="px-3 py-2 text-xs tabular-nums">{r.days.absence_days}</td>
                        <td className={`px-3 py-2 text-xs tabular-nums ${r.pending_count ? "text-amber-700 font-semibold" : ""}`} data-testid={`month-row-pending-${r.employee.id}`}>{r.awaiting_employee} / {r.awaiting_approver}</td>
                        <td className="px-3 py-2 text-xs">{fmtHours(r.hours.approved_worked_hours)} / {fmtHours(r.hours.expected_hours)}<div className="text-[10px] text-slate-500">{r.hours.comparison}</div></td>
                        <td className="px-3 py-2 text-xs tabular-nums">{r.payroll.paid_days ?? "—"}</td>
                        <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => onOpen(r)} data-testid={`month-row-open-${r.employee.id}`}>Open</Button></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function TeamMonthPanel() {
    const [period, setPeriod] = useState(currentPeriodIST());
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(null);
    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/team-month", { params: { period } });
            setRows(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Could not load your team"));
        }
    }, [period]);
    useEffect(() => { load(); }, [load]);
    return (
        <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto" data-testid="team-month-panel">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="text-sm font-semibold text-slate-900">Team month · {periodLabel(period)}</h3>
                    <p className="text-xs text-slate-500">Your direct reports. Open an employee to decide or resolve pending dates.</p>
                </div>
                <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setPeriod(shiftPeriod(period, -1))} data-testid="team-month-prev"><CaretLeft size={14} /></Button>
                    <Button size="sm" variant="outline" disabled={period >= currentPeriodIST()} onClick={() => setPeriod(shiftPeriod(period, 1))} data-testid="team-month-next"><CaretRight size={14} /></Button>
                </div>
            </div>
            <MonthRows rows={rows} onOpen={(r) => setOpen(r.employee.id)} />
            <EmployeeMonthDrawer employeeId={open} period={period} onClose={() => setOpen(null)} onChanged={load} />
        </section>
    );
}
